'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Map as ImmutableMap } from 'immutable';
import { callIfPossible } from 'UIComponents/core/Functions';
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import Small from 'UIComponents/elements/Small';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIForm from 'UIComponents/form/UIForm';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIWizard from 'UIComponents/dialog/UIWizard';
import UIWizardHeaderWithOverview from 'UIComponents/dialog/UIWizardHeaderWithOverview';
import UIWizardStep from 'UIComponents/dialog/UIWizardStep';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import PortalIdParser from 'PortalIdParser';
import { fetchPreferences } from '../apiClients/ExportPreferencesClient';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { DefaultFieldKeys, EmailValidationStates, ExportStates, FailureReasons, Formats, PreferenceState, ExportPreferences, Urls } from 'ExportDialog/Constants';
import createFormField from 'ExportDialog/components/formFields/FormField';
import EmailField from 'ExportDialog/components/formFields/EmailField';
import FormatField from 'ExportDialog/components/formFields/FormatField';
import H2 from 'UIComponents/elements/headings/H2';
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { hasGate } from '../api/AuthApi';

function getFormFieldDefaults(formFields) {
  return formFields.reduce((defaultValues, field) => {
    if (field.key) {
      defaultValues[field.key] = field.defaultValue;
    }

    return defaultValues;
  }, {});
}

class ExportDialog extends Component {
  constructor(props) {
    super();
    this.state = {
      exportPreferences: [],
      isNotificationOff: false,
      preferenceState: PreferenceState.NOT_REQUESTED,
      exportState: ExportStates.NOT_REQUESTED,
      formState: new ImmutableMap(Object.assign({}, getFormFieldDefaults(props.formFields), {
        email: props.userEmail,
        format: props.defaultFormat || Formats.CSV
      }))
    };
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.startExport = this.startExport.bind(this);
    this.export = this.export.bind(this);
    this.beginExporting = this.beginExporting.bind(this);
    this.succeedExporting = this.succeedExporting.bind(this);
    this.failExporting = this.failExporting.bind(this);
    this.handlePreferenceFetch = this.handlePreferenceFetch.bind(this);
    this.handlePreferenceFetchFailed = this.handlePreferenceFetchFailed.bind(this);
  }

  static get defaultProps() {
    return {
      formFields: [createFormField(FormatField, DefaultFieldKeys.FORMAT), createFormField(EmailField, DefaultFieldKeys.EMAIL)],
      headerContents: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "exportDialog.dialogHeading"
      }),
      use: 'DIALOG',
      exportOptions: {},
      disableExportButton: false,
      disabledExportButtonTooltipProps: null,
      // Default hack to 'hide' scroll container
      scrollHeight: 9999
    };
  }

  componentDidMount() {
    if (this.state.preferenceState === PreferenceState.NOT_REQUESTED) {
      hasGate('notification:migrateUserPrefsAPI').then(gateStatus => {
        fetchPreferences(gateStatus).then(this.handlePreferenceFetch).catch();
      }).catch(() => {
        fetchPreferences(false).then(this.handlePreferenceFetch).catch();
      });
    }
  }

  handlePreferenceFetch(exportPreferences) {
    const {
      EXPORTS_GENERAL
    } = exportPreferences.userPreferences.EXPORT;
    this.setState({
      exportPreferences: EXPORTS_GENERAL,
      preferenceState: PreferenceState.SUCCEEDED,
      isNotificationOff: EXPORTS_GENERAL.EMAIL === ExportPreferences.DO_NOT_SEND && EXPORTS_GENERAL.NOTIFICATION_SIDEBAR === ExportPreferences.DO_NOT_SEND
    });
  }

  handlePreferenceFetchFailed(err) {
    this.setState({
      preferenceState: PreferenceState.FAILED
    });
    this.failExporting(err);
  }

  handleFieldChange(key, value) {
    const {
      onFieldChange
    } = this.props;
    const {
      formState
    } = this.state;
    const newFormState = formState.set(key, value);
    this.setState({
      formState: newFormState
    });

    if (onFieldChange) {
      onFieldChange(newFormState);
    }
  }

  isExportDisabled() {
    const {
      exportState
    } = this.state;
    return exportState === ExportStates.PENDING || exportState === ExportStates.FAILED;
  }

  getEmailValidationState() {
    const {
      formState
    } = this.state;
    const email = formState.get(DefaultFieldKeys.EMAIL).trim();

    if (!EmailAddressPattern.test(email)) {
      return EmailValidationStates.INVALID;
    }

    return EmailValidationStates.VALID;
  }

  getBody() {
    const {
      use
    } = this.props;
    const loading = this.isfetchingPreferences();

    if (loading) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        minHeight: 200
      });
    }

    if (this.state.isNotificationOff) {
      return this.renderNotificationsOffModal();
    }

    if (use === 'WIZARD') {
      return this.renderWizard();
    }

    return this.renderModal();
  } //
  // Export event handler & API interaction.
  //


  isfetchingPreferences() {
    return this.state.preferenceState !== PreferenceState.SUCCEEDED || this.state.exportPreferences == null;
  }

  startExport() {
    if (this.isExportDisabled()) {
      return;
    }

    this.export();
  }

  getExportReason(xhr) {
    if (xhr.status === 0) {
      return FailureReasons.NO_NETWORK;
    }

    if (xhr.response) {
      try {
        if (JSON.parse(xhr.response).errorType === 'FREE_PORTAL_EXPORTS_LIMIT') {
          return FailureReasons.FREE_PORTAL_EXPORTS_LIMIT;
        }

        if (JSON.parse(xhr.response).errorType === 'PAID_PORTAL_EXPORTS_LIMIT') {
          return FailureReasons.PAID_PORTAL_EXPORTS_LIMIT;
        }

        if (JSON.parse(xhr.response).message === 'Portal is blocked from performing exports') {
          return FailureReasons.BLOCKED_PORTAL;
        }
      } catch (e) {// ignore
      }
    }

    return FailureReasons.UNKNOWN;
  }

  export() {
    const {
      client,
      exportOptions
    } = this.props;
    const {
      formState
    } = this.state;
    const options = Object.assign({}, formState.toJS(), {}, exportOptions);
    this.beginExporting();
    return client.export(options).then(this.succeedExporting, ({
      xhr = {}
    } = {}) => {
      const failureReason = this.getExportReason(xhr);
      return this.failExporting(failureReason, xhr);
    });
  }

  beginExporting() {
    this.setState({
      exportState: ExportStates.PENDING
    });
  }

  succeedExporting() {
    const {
      onClose,
      onSuccess
    } = this.props;
    this.setState({
      exportState: ExportStates.SUCCEEDED
    }, () => {
      onClose();
      callIfPossible(onSuccess);
    });
  }

  failExporting(failureReason = FailureReasons.UNKNOWN, xhr) {
    const {
      onClose,
      onFailure
    } = this.props;
    this.setState({
      exportState: ExportStates.FAILED
    }, () => {
      if (failureReason === FailureReasons.FREE_PORTAL_EXPORTS_LIMIT || failureReason === FailureReasons.PAID_PORTAL_EXPORTS_LIMIT) {
        try {
          const limit = JSON.parse(xhr.response).errorTokens.totalExportsLimit[0];
          FloatingAlertStore.addAlert({
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "exportDialog.limitPortalAlertMessage",
              options: {
                limit
              }
            }),
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "exportDialog.limitPortalAlertTitle"
            }),
            type: 'danger'
          });
        } catch (e) {
          // unable to determine limit
          FloatingAlertStore.addAlert({
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "exportDialog.limitPortalAlertMessageUnknownLimit"
            }),
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "exportDialog.limitPortalAlertTitle"
            }),
            type: 'danger'
          });
        }

        return;
      }

      if (failureReason === FailureReasons.BLOCKED_PORTAL) {
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.blockedPortalAlertMessage"
          }),
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.blockedPortalAlertTitle"
          }),
          type: 'danger'
        });
        return;
      }

      onClose();
      callIfPossible(onFailure, failureReason, xhr);
    });
  } //
  // Rendering methods.
  //


  renderFormFields() {
    const {
      formFields
    } = this.props;
    const {
      formState,
      exportPreferences
    } = this.state;
    return formFields.map(Field => {
      const value = formState.get(Field.key);
      const additionalProps = {};

      if (Field.key === DefaultFieldKeys.EMAIL) {
        additionalProps.validationState = this.getEmailValidationState();
      }

      return /*#__PURE__*/_jsx(Field, Object.assign({
        value: value,
        onChange: this.handleFieldChange,
        preferences: exportPreferences
      }, additionalProps), Field.key);
    });
  }

  renderEmailFieldAndNotificationHelpText() {
    const {
      emailFieldAndNotificationHelpText
    } = this.props;
    const Field = emailFieldAndNotificationHelpText;
    const {
      formState,
      exportPreferences
    } = this.state;
    const value = formState.get(emailFieldAndNotificationHelpText.key);
    const additionalProps = {};
    additionalProps.validationState = this.getEmailValidationState();
    return /*#__PURE__*/_jsx(Field, Object.assign({
      value: value,
      onChange: this.handleFieldChange,
      preferences: exportPreferences
    }, additionalProps), emailFieldAndNotificationHelpText.key);
  }

  renderEmailHelpText() {
    return /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "exportDialog.allowlistHelpText_jsx",
          elements: {
            KBLink: KnowledgeBaseButton
          },
          options: {
            allowlistUrl: {
              url: Urls.allowlistUrl,
              className: 'm-all-0'
            },
            notificationUrl: {
              url: Urls.notificationUrl,
              className: 'm-all-0'
            }
          }
        })
      })
    });
  }

  renderWizard() {
    const {
      onReject,
      children,
      firstStepProps
    } = this.props;
    const hasValidEmail = this.getEmailValidationState() === EmailValidationStates.VALID;
    return /*#__PURE__*/_jsxs(UIWizard, {
      cancellable: true,
      defaultHeading: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "exportWizard.title"
      }),
      headerComponent: UIWizardHeaderWithOverview,
      onReject: onReject,
      onConfirm: this.startExport,
      disablePrimaryButton: !hasValidEmail || this.isExportDisabled(),
      children: [/*#__PURE__*/_jsx(UIWizardStep, Object.assign({
        name: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "exportWizard.details"
        })
      }, firstStepProps, {
        children: /*#__PURE__*/_jsx(UIFlex, {
          justify: "center",
          children: /*#__PURE__*/_jsxs(UIBox, {
            basis: "650px",
            children: [/*#__PURE__*/_jsx(UIForm, {
              className: "m-bottom-6",
              children: this.renderFormFields()
            }), this.renderEmailHelpText()]
          })
        })
      })), children]
    });
  }

  renderModal() {
    const {
      exportState
    } = this.state;
    const {
      onClose,
      topBodyContents,
      topNavBar,
      bottomBodyContents,
      disableExportButton,
      disabledExportButtonTooltipProps,
      emailFieldAndNotificationHelpText,
      scrollHeight
    } = this.props;
    const hasValidEmail = this.getEmailValidationState() === EmailValidationStates.VALID;
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": "modal-container",
      children: [/*#__PURE__*/_jsx(UIScrollContainer, {
        maxHeight: scrollHeight,
        children: /*#__PURE__*/_jsxs(UIDialogBody, {
          children: [topNavBar, emailFieldAndNotificationHelpText && this.renderEmailFieldAndNotificationHelpText(), topBodyContents, /*#__PURE__*/_jsx(UIForm, {
            className: "m-bottom-6",
            children: this.renderFormFields()
          }), bottomBodyContents, !emailFieldAndNotificationHelpText && this.renderEmailHelpText()]
        })
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UITooltip, Object.assign({
          disabled: !disableExportButton || disabledExportButtonTooltipProps === null
        }, disabledExportButtonTooltipProps, {
          children: /*#__PURE__*/_jsx(UILoadingButton, {
            "data-selenium-test": "ExportDialog-submit-button",
            use: "primary",
            onClick: this.startExport,
            loading: exportState === ExportStates.PENDING,
            disabled: !hasValidEmail || this.isExportDisabled() || disableExportButton,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "exportDialog.exportCta"
            })
          })
        })), /*#__PURE__*/_jsx(UIButton, {
          "data-selenium-test": "ExportDialog-cancel-button",
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.cancel"
          })
        })]
      })]
    });
  }

  renderNotificationsOffModal() {
    const {
      onClose
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx("h2", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.preference.notificationsOffHeading"
          })
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "exportDialog.preference.notificationsOffText"
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          href: `/user-preferences/${PortalIdParser.get()}/global-preferences`,
          external: true,
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.preference.notificationCta"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.cancel"
          })
        })]
      })]
    });
  }

  render() {
    const _this$props = this.props,
          {
      onClose,
      headerContents,
      use
    } = _this$props,
          otherProps = _objectWithoutPropertiesLoose(_this$props, ["onClose", "headerContents", "use", "disabledExportButtonTooltipProps", "disableExportButton", "exportOptions", "userEmail", "formFields", "defaultFormat", "scrollHeight"]);

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {}), /*#__PURE__*/_jsxs(UIModal, Object.assign({
        "data-selenium-test": "export-dialog",
        use: use === 'WIZARD' ? 'fullscreen' : 'default'
      }, otherProps, {
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: onClose
          }), /*#__PURE__*/_jsx(H2, {
            children: headerContents
          })]
        }), this.getBody()]
      }))]
    });
  }

}

ExportDialog.propTypes = {
  // The API client to use needs to implement these methods.
  client: PropTypes.shape({
    export: PropTypes.func.isRequired
  }).isRequired,
  // The form fields to be displayed. They must be wrapped by `createFormField`.
  formFields: PropTypes.arrayOf(PropTypes.func).isRequired,
  // The contents of the header at the top of the dialog.
  headerContents: PropTypes.node.isRequired,
  // Navigation bar at the top of the dialog
  topNavBar: PropTypes.node,
  // Additional content at the top of the dialog body.
  topBodyContents: PropTypes.node,
  // Additional content at the bottom of the dialog body.
  bottomBodyContents: PropTypes.node,
  // Tells the parent to unmount the dialog.
  onClose: PropTypes.func.isRequired,
  // Lets the parent display a failure message.
  onFailure: PropTypes.func,
  // Lets the parent display a success message.
  onSuccess: PropTypes.func,
  // The email of the user requesting the export.
  userEmail: PropTypes.string.isRequired,
  // The type of export dialog to render.
  use: PropTypes.oneOf(['WIZARD', 'DIALOG']).isRequired,
  // Props that are passed to the first step in the UIWizard
  firstStepProps: PropTypes.object,
  // Used as the rest of the steps in UIWizard
  children: PropTypes.node,
  // Extra options passed from the parent to the export call.
  exportOptions: PropTypes.object,
  // EmailFieldAndNotificationHelpText at the top of the dialog
  emailFieldAndNotificationHelpText: PropTypes.func,
  onReject: PropTypes.func,
  // Lets the parent know a field has changed.
  onFieldChange: PropTypes.func,
  // Manually disable the export primary action
  disableExportButton: PropTypes.bool,
  // To provide the disabled button tooltip props
  disabledExportButtonTooltipProps: PropTypes.object,
  // Default values for fields
  defaultFormat: PropTypes.string,
  // Defines scroll container height for body contents
  scrollHeight: PropTypes.number
};
export default ExportDialog;